import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Typography } from '@recruit-robin/robin-theme/web-components';
import { Check } from '@recruit-robin/robin-theme/web-icons';
import { NylasScheduling } from '@nylas/react';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useStyles } from './RJCommHubQuestionnaireConfirmation.styles';

export const RJCommHubQuestionnaireConfirmation = () => {
  const { dictionary, language } = useContext(LocalizationContext);
  const location = useLocation();
  const schedulerLink = location.state?.schedulerLink as string | undefined;

  const classes = useStyles();

  const { clientId, slug, identifier, name, email, phone, jobTitle, schedulerApiUrl, schedulerType } = useMemo(() => {
    const emptyState = {
      clientId: '',
      slug: '',
      identifier: '',
      name: '',
      email: '',
      phone: '',
      jobTitle: '',
      schedulerApiUrl: '',
      schedulerType: '',
    };

    if (!schedulerLink) return emptyState;

    try {
      const url = new URL(schedulerLink);
      const [region, clientId, slug] = url.pathname.split('/').filter(Boolean);

      const searchParams = new URLSearchParams(url.search);
      const identifier = searchParams.get('identifier') || '';
      const name = searchParams.get('name') || '';
      const email = searchParams.get('email') || '';
      const phone = searchParams.get('phone') || '';
      const jobTitle = searchParams.get('job_title') || '';
      const schedulerType = searchParams.get('scheduler_type') || '';

      const normalizedRegion = region?.toLowerCase() || 'eu';
      const schedulerApiUrl = normalizedRegion ? `https://api.${normalizedRegion}.nylas.com` : '';

      return { clientId, slug, identifier, name, email, phone, jobTitle, schedulerApiUrl, schedulerType };
    } catch {
      return emptyState;
    }
  }, [schedulerLink]);

  const canRenderScheduler = Boolean(clientId && slug && schedulerApiUrl);

  return (
    <div css={classes.wrapper}>
      {canRenderScheduler ? (
        <div css={classes.schedulerContainer}>
          <NylasScheduling
            clientId={clientId}
            slug={slug}
            schedulerApiUrl={schedulerApiUrl}
            themeConfig={{
              '--nylas-primary': '#E56CBF',
              '--nylas-base-500': '#C80E8D',
              '--nylas-base-600': '#C80E8D',
            }}
            bookingInfo={{
              language: language,
              primaryParticipant: {
                name: name,
                email: email,
              },
              additionalFields: {
                identifier: {
                  value: identifier,
                  type: 'metadata',
                },
                phone: {
                  value: phone,
                  type: 'phone_number',
                },
                job_title: {
                  value: jobTitle,
                  type: 'text',
                  readOnly: true,
                },
                scheduler_type: {
                  value: schedulerType,
                  type: 'metadata',
                },
              },
            }}
          />
        </div>
      ) : (
        <div css={classes.messageContainerWrapper}>
          <div css={classes.messageContainer}>
            <div css={classes.iconContainer}>
              <Check size={24} />
            </div>
            <div css={classes.textContainer}>
              <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
                {dictionary.questionnaireCompleteTitle}
              </Typography>
              <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
                {dictionary.questionnaireCompleteMessage}
              </Typography>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RJCommHubQuestionnaireConfirmation;
